import type { DuplicateRef, Item, Note, Position, Room, ShopLink } from "./Api.js";

/**
 * Vergleich zweier Instanzen desselben Inventars.
 *
 * Dieselbe Aufstellung läuft an mehreren Orten; auf einer davon wurde
 * weitergearbeitet. Hier entsteht aus zwei Datenständen die Liste der
 * Unterschiede — reine Rechnung, keine Anzeige: views.ts macht daraus die
 * Ansicht, InventoryApp.ts holt die Gegenseite (Api.fetchReferenceSnapshot).
 *
 * Die Datei importiert bewusst **nichts** aus views.ts: Dort wird compare.ts
 * gebraucht, und ein Zirkel zwischen beiden wäre nur eine Frage der Zeit, bis
 * er bei der Initialisierung zuschlägt. Die paar Formatierungen, die es hier
 * braucht, stehen deshalb unten als eigene kleine Helfer.
 */

/* --------------------------------------------------------- Datenstand */

/**
 * Ein Datenstand, wie ihn beide Seiten liefern.
 *
 * Die Gegenseite baut ihn in PHP (Snapshot.php im Modul inventory_compare),
 * die eigene Seite entsteht aus den geladenen Daten der App
 * (buildLocalSnapshot). Beide Wege müssen dieselbe Form ergeben — sonst
 * vergleicht man Feldnamen statt Inhalte.
 *
 * Fotos und Bildausschnitte fehlen mit Absicht: Anhang-IDs werden je Instanz
 * vergeben, ein Vergleich wäre reines Rauschen.
 */
export interface Snapshot {
    schema: number;
    site: { name: string; url: string; version: string; erzeugt: string };
    raeume: SnapshotRoom[];
    positionen: SnapshotPosition[];
    gegenstaende: SnapshotItem[];
    hinweise: SnapshotNote[];
}

/** Formatversion, die diese Fassung lesen kann. */
export const SNAPSHOT_SCHEMA = 1;

export interface SnapshotRoom {
    id: number;
    titel: string;
    order: number;
    beschreibung: string;
}

export interface SnapshotPosition {
    id: number;
    titel: string;
    nummer: number;
    raum: number;
    beschreibung: string;
    imageKey: string;
    importLog: string;
}

export interface SnapshotItem {
    id: number;
    titel: string;
    order: number;
    position: number;
    anzahl: number;
    wert: number;
    versandkosten: number;
    alter: string;
    datum: string;
    quelle: string;
    shopLinks: ShopLink[];
    wertBegruendung: string;
    sonstiges: string;
    importLog: string;
    geprueft: boolean;
    duplikatStatus: string;
    duplikatRefs: DuplicateRef[];
}

export interface SnapshotNote {
    id: number;
    titel: string;
    order: number;
    level: string;
    text: string;
    erledigt: boolean;
    imageKey: string;
    raum: number;
    position: number;
    item: number;
}

/** Der eigene Datenstand in derselben Form wie der der Gegenseite. */
export function buildLocalSnapshot(
    daten: { rooms: Room[]; positions: Position[]; items: Item[]; notes: Note[] },
    site: Snapshot["site"],
): Snapshot {
    return {
        schema: SNAPSHOT_SCHEMA,
        site,
        raeume: daten.rooms.map((r) => ({
            id: r.id,
            titel: r.title,
            order: r.order,
            beschreibung: r.beschreibung,
        })),
        positionen: daten.positions.map((p) => ({
            id: p.id,
            titel: p.title,
            nummer: p.nummer,
            raum: p.raum,
            beschreibung: p.beschreibung,
            imageKey: p.imageKey,
            importLog: p.importLog,
        })),
        gegenstaende: daten.items.map((i) => ({
            id: i.id,
            titel: i.title,
            order: i.order,
            position: i.position,
            anzahl: i.anzahl,
            wert: i.wert,
            versandkosten: i.versandkosten,
            alter: i.alter,
            datum: i.datum,
            quelle: i.quelle,
            shopLinks: i.shopLinks,
            wertBegruendung: i.wertBegruendung,
            sonstiges: i.sonstiges,
            importLog: i.importLog,
            geprueft: i.geprueft,
            duplikatStatus: i.duplikatStatus,
            duplikatRefs: i.duplikatRefs,
        })),
        hinweise: daten.notes.map((n) => ({
            id: n.id,
            titel: n.title,
            order: n.order,
            level: n.level,
            text: n.text,
            erledigt: n.erledigt,
            imageKey: n.imageKey,
            raum: n.raum,
            position: n.position,
            item: n.item,
        })),
    };
}

/* ------------------------------------------------------------ Ergebnis */

export type CompareKind = "item" | "position" | "room" | "note";

/**
 * Was mit einem Datensatz zwischen den beiden Ständen passiert ist.
 *
 * "konflikt" ist der Sonderfall geklonter Datenbanken: Beide Seiten vergeben
 * ihre Nummern danach selbst weiter, dieselbe Nummer kann also auf zwei völlig
 * verschiedene Datensätze zeigen. Das als Änderung auszugeben, wäre die
 * schlimmste Variante — es sähe aus wie ein umbenannter Gegenstand.
 */
export type CompareCategory = "neu" | "fehlt" | "geaendert" | "konflikt";

export interface FieldDiff {
    key: string;
    label: string;
    /** Fertig lesbar; eine Liste steht für mehrzeilige Werte (Shop-Links). */
    hier: string | string[];
    referenz: string | string[];
    /** Nebensache wie Reihenfolge oder Import-Protokoll — standardmäßig aus. */
    minor: boolean;
}

export interface CompareEntry {
    kind: CompareKind;
    id: number;
    titel: string;
    /** Fundort in Worten, z. B. „Nr. 241 · Arbeitszimmer Stefan". */
    ort: string;
    kategorie: CompareCategory;
    felder: FieldDiff[];
    /**
     * Wertunterschied dieser Zeile in Euro (Referenz minus hier), damit sich
     * eine Preisänderung sofort einordnen lässt. Nur bei Gegenständen ≠ 0.
     */
    wertDelta: number;
}

export interface CompareSummary {
    kategorien: Record<CompareCategory, number>;
    typen: Record<CompareKind, number>;
    /** Datensätze, die auf beiden Seiten gleich sind. */
    gleich: number;
    /** Versicherungssumme je Seite (bestätigte Duplikate zählen nicht mit). */
    wertHier: number;
    wertReferenz: number;
}

export interface CompareResult {
    entries: CompareEntry[];
    summary: CompareSummary;
}

/* ----------------------------------------------------------- Vergleich */

type CompareValue = string | number | boolean | string[];

interface FieldSpec<T> {
    key: string;
    label: string;
    /** Der Wert, an dem „gleich oder nicht" entschieden wird. */
    value: (rec: T, seite: SnapshotIndex) => CompareValue;
    /**
     * Verweis auf einen anderen Datensatz (Position, Raum, Gegenstand).
     *
     * Solche Felder gelten als gleich, sobald **entweder** die ID **oder** die
     * aufgelöste Bezeichnung übereinstimmt. Beides zusammen ist nötig, weil
     * jede der beiden Prüfungen allein danebenliegt: Über die ID allein wäre
     * bei auseinandergelaufenen Nummern jede Zuordnung "geändert"; über die
     * Bezeichnung allein schlägt ein umbenannter Raum auf jeden Gegenstand
     * darin durch — bei einem einzigen umbenannten Raum waren das in der
     * Erprobung 450 Scheinänderungen.
     */
    ref?: (rec: T) => number;
    minor?: boolean;
}

/**
 * Nachschlagewerk zu einem Datenstand.
 *
 * Verweise werden über die aufgelöste Bezeichnung verglichen, nie über die ID:
 * Zeigen beide Seiten auf „Nr. 12 · Wohnzimmer", ist das dieselbe Position —
 * auch wenn sie dort eine andere Post-ID trägt. Ohne diese Regel bestünde bei
 * auseinandergelaufenen Nummern der halbe Vergleich aus Scheinänderungen.
 */
interface SnapshotIndex {
    rooms: Map<number, SnapshotRoom>;
    positions: Map<number, SnapshotPosition>;
    items: Map<number, SnapshotItem>;
}

function index(snap: Snapshot): SnapshotIndex {
    return {
        rooms: new Map(snap.raeume.map((r) => [r.id, r])),
        positions: new Map(snap.positionen.map((p) => [p.id, p])),
        items: new Map(snap.gegenstaende.map((i) => [i.id, i])),
    };
}

function roomLabel(idx: SnapshotIndex, id: number): string {
    if (!id) return "";
    return idx.rooms.get(id)?.titel || `#${id}`;
}

function positionLabel(idx: SnapshotIndex, id: number): string {
    if (!id) return "";
    const position = idx.positions.get(id);
    if (!position) return `#${id}`;
    const raum = roomLabel(idx, position.raum);
    const nummer = position.nummer ? `Nr. ${position.nummer} · ` : "";
    return `${nummer}${position.titel}${raum ? ` · ${raum}` : ""}`;
}

function itemLabel(idx: SnapshotIndex, id: number): string {
    if (!id) return "";
    return idx.items.get(id)?.titel || `#${id}`;
}

const ROOM_FIELDS: FieldSpec<SnapshotRoom>[] = [
    { key: "titel", label: "Bezeichnung", value: (r) => text(r.titel) },
    { key: "beschreibung", label: "Beschreibung", value: (r) => text(r.beschreibung) },
    { key: "order", label: "Reihenfolge", value: (r) => r.order, minor: true },
];

const POSITION_FIELDS: FieldSpec<SnapshotPosition>[] = [
    { key: "titel", label: "Bezeichnung", value: (p) => text(p.titel) },
    { key: "nummer", label: "Positionsnummer", value: (p) => p.nummer },
    { key: "raum", label: "Raum", value: (p, seite) => roomLabel(seite, p.raum), ref: (p) => p.raum },
    { key: "beschreibung", label: "Beschreibung", value: (p) => text(p.beschreibung) },
    { key: "imageKey", label: "Bilddatei", value: (p) => text(p.imageKey), minor: true },
    { key: "importLog", label: "Import-Protokoll", value: (p) => text(p.importLog), minor: true },
];

const ITEM_FIELDS: FieldSpec<SnapshotItem>[] = [
    { key: "titel", label: "Bezeichnung", value: (i) => text(i.titel) },
    { key: "position", label: "Position", value: (i, seite) => positionLabel(seite, i.position), ref: (i) => i.position },
    { key: "anzahl", label: "Anzahl", value: (i) => i.anzahl },
    { key: "wert", label: "Wert je Stück", value: (i) => euro(i.wert) },
    { key: "versandkosten", label: "Versandkosten", value: (i) => euro(i.versandkosten) },
    { key: "alter", label: "Alter", value: (i) => text(i.alter) },
    { key: "datum", label: "Datum", value: (i) => text(i.datum) },
    { key: "quelle", label: "Quelle", value: (i) => text(i.quelle) },
    { key: "shopLinks", label: "Vergleichsangebote", value: (i) => shopLinkLines(i.shopLinks) },
    { key: "wertBegruendung", label: "Wertbegründung", value: (i) => text(i.wertBegruendung) },
    { key: "sonstiges", label: "Sonstiges", value: (i) => text(i.sonstiges) },
    { key: "geprueft", label: "Geprüft", value: (i) => janein(i.geprueft) },
    { key: "duplikatStatus", label: "Duplikat-Status", value: (i) => duplikatLabel(i.duplikatStatus) },
    {
        key: "duplikatRefs",
        label: "Duplikat-Verweise",
        value: (i, seite) => i.duplikatRefs.map((r) => duplikatRefLine(seite, r)).sort(),
    },
    { key: "order", label: "Reihenfolge", value: (i) => i.order, minor: true },
    { key: "importLog", label: "Import-Protokoll", value: (i) => text(i.importLog), minor: true },
];

const NOTE_FIELDS: FieldSpec<SnapshotNote>[] = [
    { key: "titel", label: "Titel", value: (n) => text(n.titel) },
    { key: "level", label: "Stufe", value: (n) => text(n.level) },
    { key: "text", label: "Text", value: (n) => text(n.text) },
    { key: "erledigt", label: "Erledigt", value: (n) => janein(n.erledigt) },
    { key: "imageKey", label: "Bilddatei", value: (n) => text(n.imageKey), minor: true },
    { key: "raum", label: "Raum", value: (n, seite) => roomLabel(seite, n.raum), ref: (n) => n.raum },
    { key: "position", label: "Position", value: (n, seite) => positionLabel(seite, n.position), ref: (n) => n.position },
    { key: "item", label: "Gegenstand", value: (n, seite) => itemLabel(seite, n.item), ref: (n) => n.item },
];

/**
 * Stellt zwei Datenstände gegenüber.
 *
 * Zugeordnet wird über die Post-ID: Die Instanzen sind Kopien derselben
 * Datenbank, dieselbe Nummer meint also in aller Regel denselben Datensatz.
 * Wo das erkennbar nicht mehr stimmt, entsteht ein „konflikt" statt einer
 * erfundenen Änderung (siehe istKonflikt).
 */
export function diffSnapshots(hier: Snapshot, referenz: Snapshot): CompareResult {
    const hierIdx = index(hier);
    const refIdx = index(referenz);
    const entries: CompareEntry[] = [];
    const summary: CompareSummary = {
        kategorien: { neu: 0, fehlt: 0, geaendert: 0, konflikt: 0 },
        typen: { item: 0, position: 0, room: 0, note: 0 },
        gleich: 0,
        wertHier: inventarwert(hier),
        wertReferenz: inventarwert(referenz),
    };

    const sammeln = <T extends { id: number; titel: string }>(
        kind: CompareKind,
        hierListe: T[],
        refListe: T[],
        felder: FieldSpec<T>[],
        ort: (rec: T, seite: SnapshotIndex) => string,
    ) => {
        const hierMap = new Map(hierListe.map((r) => [r.id, r]));
        const refMap = new Map(refListe.map((r) => [r.id, r]));

        for (const id of new Set([...hierMap.keys(), ...refMap.keys()])) {
            const a = hierMap.get(id);
            const b = refMap.get(id);

            if (a && !b) {
                // Die Referenz ist um diesen Zeilenwert ärmer — mit dem
                // Vorzeichen addieren sich alle Deltas zur Gesamtdifferenz.
                entries.push(eintrag(kind, a, "fehlt", [], ort(a, hierIdx), -zeilenwert(kind, a)));
                continue;
            }
            if (!a && b) {
                entries.push(eintrag(kind, b, "neu", [], ort(b, refIdx), zeilenwert(kind, b)));
                continue;
            }
            if (!a || !b) continue;

            const felderDiff: FieldDiff[] = [];
            for (const feld of felder) {
                // Gleiche ID heißt gleiche Zuordnung — ohne die Bezeichnung
                // überhaupt anzusehen (siehe FieldSpec.ref).
                if (feld.ref && feld.ref(a) === feld.ref(b)) continue;
                const links = feld.value(a, hierIdx);
                const rechts = feld.value(b, refIdx);
                if (gleich(links, rechts)) continue;
                felderDiff.push({
                    key: feld.key,
                    label: feld.label,
                    hier: anzeige(links),
                    referenz: anzeige(rechts),
                    minor: !!feld.minor,
                });
            }

            if (felderDiff.length === 0) {
                summary.gleich++;
                continue;
            }
            const kategorie = istKonflikt(kind, a, b, hierIdx, refIdx) ? "konflikt" : "geaendert";
            entries.push(eintrag(
                kind,
                a,
                kategorie,
                felderDiff,
                ort(a, hierIdx),
                zeilenwert(kind, b) - zeilenwert(kind, a),
            ));
        }
    };

    sammeln("item", hier.gegenstaende, referenz.gegenstaende, ITEM_FIELDS, (i, seite) => positionLabel(seite, i.position));
    sammeln("position", hier.positionen, referenz.positionen, POSITION_FIELDS, (p, seite) => roomLabel(seite, p.raum));
    sammeln("room", hier.raeume, referenz.raeume, ROOM_FIELDS, () => "");
    sammeln("note", hier.hinweise, referenz.hinweise, NOTE_FIELDS, (n, seite) => positionLabel(seite, n.position) || roomLabel(seite, n.raum));

    for (const entry of entries) {
        summary.kategorien[entry.kategorie]++;
        summary.typen[entry.kind]++;
    }

    entries.sort(vergleichReihenfolge);
    return { entries, summary };
}

function eintrag(
    kind: CompareKind,
    rec: { id: number; titel: string },
    kategorie: CompareCategory,
    felder: FieldDiff[],
    ort: string,
    wertDelta: number,
): CompareEntry {
    return { kind, id: rec.id, titel: rec.titel, ort, kategorie, felder, wertDelta: round2(wertDelta) };
}

/** Konflikte zuerst: Sie stellen das Ergebnis selbst infrage. */
const KATEGORIE_RANG: Record<CompareCategory, number> = { konflikt: 0, neu: 1, fehlt: 2, geaendert: 3 };
const TYP_RANG: Record<CompareKind, number> = { item: 0, position: 1, room: 2, note: 3 };

function vergleichReihenfolge(a: CompareEntry, b: CompareEntry): number {
    return KATEGORIE_RANG[a.kategorie] - KATEGORIE_RANG[b.kategorie]
        || TYP_RANG[a.kind] - TYP_RANG[b.kind]
        || a.ort.localeCompare(b.ort, "de")
        || a.titel.localeCompare(b.titel, "de");
}

/**
 * Erkennt, dass zwei Datensätze mit derselben Nummer gar nicht derselbe sind.
 *
 * Nach dem Klonen zählen beide Instanzen ihre Nummern getrennt weiter: Was
 * hier Gegenstand #9600 ist, kann dort ein völlig anderer sein. Entscheidend
 * ist die Und-Verknüpfung — ein umbenannter Gegenstand an derselben Position
 * ist eine Änderung (genau der Normalfall), erst wenn auch der Fundort nicht
 * mehr passt, sind es zwei verschiedene Einträge.
 *
 * Räume und Hinweise bleiben außen vor: Ein Raum hat außer seinem Namen kein
 * zweites Merkmal, eine Umbenennung wäre von einem Konflikt nicht zu
 * unterscheiden.
 */
function istKonflikt(
    kind: CompareKind,
    a: any,
    b: any,
    hierIdx: SnapshotIndex,
    refIdx: SnapshotIndex,
): boolean {
    const fremd = aehnlichkeit(String(a.titel), String(b.titel)) < 0.4;
    if (!fremd) return false;

    if (kind === "item") {
        return positionLabel(hierIdx, a.position) !== positionLabel(refIdx, b.position);
    }
    if (kind === "position") {
        return a.nummer !== b.nummer;
    }
    return false;
}

/**
 * Ähnlichkeit zweier Bezeichnungen zwischen 0 und 1 (Dice-Koeffizient über
 * Zeichenpaare). Verträgt Tippfehler und Umformulierungen und braucht keine
 * Wortliste — „Ledersofa 3-Sitzer" und „Ledersofa, 3-Sitzer, braun" liegen
 * damit nah beieinander, „Bohrmaschine" und „Winterjacke" weit auseinander.
 */
function aehnlichkeit(a: string, b: string): number {
    const links = paare(a.toLowerCase());
    const rechts = paare(b.toLowerCase());
    if (links.length === 0 || rechts.length === 0) return a === b ? 1 : 0;

    const vorrat = new Map<string, number>();
    for (const paar of links) vorrat.set(paar, (vorrat.get(paar) ?? 0) + 1);

    let treffer = 0;
    for (const paar of rechts) {
        const offen = vorrat.get(paar) ?? 0;
        if (offen > 0) {
            vorrat.set(paar, offen - 1);
            treffer++;
        }
    }
    return (2 * treffer) / (links.length + rechts.length);
}

function paare(wert: string): string[] {
    const sauber = wert.replace(/[^\p{L}\p{N}]+/gu, " ").trim();
    const ergebnis: string[] = [];
    for (let i = 0; i < sauber.length - 1; i++) {
        ergebnis.push(sauber.slice(i, i + 2));
    }
    return ergebnis;
}

/** Zeilenwert eines Gegenstands; alles andere trägt keinen Betrag. */
function zeilenwert(kind: CompareKind, rec: any): number {
    if (kind !== "item") return 0;
    return zaehltMit(rec) ? rec.wert * rec.anzahl : 0;
}

/**
 * Spiegelt countsAsValue() aus views.ts: Ein bestätigtes Duplikat bleibt in
 * den Daten, zählt aber in keiner Summe. Bewusst doppelt statt importiert —
 * compare.ts darf views.ts nicht einbinden (Zirkel).
 */
function zaehltMit(item: SnapshotItem): boolean {
    return item.duplikatStatus !== "bestaetigt";
}

function inventarwert(snap: Snapshot): number {
    return round2(snap.gegenstaende.reduce((summe, i) => summe + (zaehltMit(i) ? i.wert * i.anzahl : 0), 0));
}

/* ------------------------------------------------------------- Helfer */

function gleich(a: CompareValue, b: CompareValue): boolean {
    if (Array.isArray(a) || Array.isArray(b)) {
        const links = Array.isArray(a) ? a : [String(a)];
        const rechts = Array.isArray(b) ? b : [String(b)];
        return links.length === rechts.length && links.every((wert, i) => wert === rechts[i]);
    }
    return a === b;
}

function anzeige(wert: CompareValue): string | string[] {
    if (Array.isArray(wert)) return wert;
    if (typeof wert === "boolean") return janein(wert);
    return String(wert);
}

/**
 * Vereinheitlicht einen Text vor dem Vergleich.
 *
 * Zeilenenden und Leerraum am Rand unterscheiden sich zwischen den Instanzen
 * schon deshalb, weil die Daten einmal über die REST-API und einmal roh aus
 * dem Postmeta kommen. Ohne diese Angleichung meldete jeder längere Text einen
 * Unterschied, den niemand sehen kann.
 */
function text(wert: unknown): string {
    return String(wert ?? "")
        .normalize("NFC")
        .replace(/\r\n?/g, "\n")
        .replace(/[ \t]+$/gm, "")
        .trim();
}

/** Geldbetrag als Text — auf Cent gerundet, damit 29.700000000000003 nie auffällt. */
function euro(wert: number): string {
    return round2(wert).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function round2(wert: number): number {
    return Math.round((Number(wert) || 0) * 100) / 100;
}

function janein(wert: boolean): string {
    return wert ? "Ja" : "Nein";
}

const DUPLIKAT_LABEL: Record<string, string> = {
    "": "",
    moeglich: "Möglicher Doppeleintrag",
    bestaetigt: "Bestätigtes Duplikat",
    kein_duplikat: "Kein Duplikat",
};

function duplikatLabel(status: string): string {
    return DUPLIKAT_LABEL[status] ?? status;
}

/**
 * Vergleichsangebote als Zeilen — sortiert, weil die Reihenfolge nichts
 * bedeutet: Ein umsortiertes Angebot ist keine Änderung.
 */
function shopLinkLines(links: ShopLink[]): string[] {
    return links
        .map((l) => `${l.url} · ${euro(l.preis)}${l.datum ? ` · ${l.datum}` : ""}`)
        .sort();
}

function duplikatRefLine(seite: SnapshotIndex, ref: DuplicateRef): string {
    const ziel = itemLabel(seite, ref.item) || positionLabel(seite, ref.position) || `#${ref.item}`;
    return ref.note ? `${ziel}: ${ref.note}` : ziel;
}
